<template>
  <!-- #1533: opened from the wishlist-summary report's per-row link. Presents the
       assign offers as a popup/modal (the report is cross-origin + arjs is frozen,
       so the overlay lives on this cs3-web page). When opened straight from the
       report link it relaunches itself as a sized popup window (gap 1); the popup
       carries ?embed=1 and renders the modal card below. -->
  <div v-if="launched" class="wl-assign wl-assign--launch">
    <div class="wl-assign__launchmsg">
      Opening the assignment window…<br />
      <small>You can close this tab. If nothing opened, allow popups and click the name again.</small>
    </div>
  </div>

  <div v-else class="wl-assign" @click.self="closeWindow">
    <div class="wl-assign__card" role="dialog" aria-modal="true">
      <div class="wl-assign__head">
        <h2>Assign open slot</h2>
        <a v-if="canClose" href="javascript:void(0)" class="wl-assign__close" @click="closeWindow">&times;</a>
      </div>

      <p v-if="employeeName" class="wl-assign__emp">{{ employeeName }}</p>

      <div v-if="error" class="wl-assign__error">{{ error }}</div>
      <div v-if="success" class="wl-assign__success">{{ success }}</div>

      <!-- #1533: post-assign navigation (ed's spec). No Close — "Wish List" is the
           return-to-WL. Wish List / Reload force a fresh report JSON in place. -->
      <div v-if="assigned" class="wl-assign__nav">
        <p class="wl-assign__navhint">Where to next?</p>
        <button type="button" class="wl-assign__navbtn wl-assign__navbtn--primary" @click="navTo('refresh')">Reload WL</button>
        <button type="button" class="wl-assign__navbtn" @click="navTo('phonesheet')">Phone Sheet</button>
        <button type="button" class="wl-assign__navbtn" @click="navTo('calllist')">Calls</button>
        <button type="button" class="wl-assign__navbtn" @click="navTo('joblist')">Job List</button>
      </div>

      <template v-else>
        <div v-if="loading" class="wl-assign__note">Loading open slots…</div>
        <div v-else-if="slots.length === 0 && !error" class="wl-assign__note">
          No open slots this employee is skilled for.
        </div>

        <ul v-else class="wl-assign__slots">
          <li v-for="slot in slots" :key="slot.id" class="wl-assign__slot">
            <span class="wl-assign__slotinfo">
              <strong>{{ slot.skill || 'No required skill' }}</strong>
              <em>{{ formatSlot(slot) }}</em>
            </span>
            <a href="javascript:void(0)"
               class="wl-assign__btn"
               :class="{ 'is-busy': assigningId === slot.id }"
               @click="assign(slot)">
              {{ assigningId === slot.id ? '…' : 'Assign' }}
            </a>
          </li>
        </ul>
      </template>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import service from '../../services.js';

// same-origin channel the report opener (jobDetails) listens on to reload the
// cross-origin report viewer after an assign (gap 2).
const WL_CHANNEL = 'cs3-wishlist-assign';

export default {
  name: 'WishlistAssign',
  data() {
    return {
      jobId: '',
      employeeId: '',
      employeeName: '',
      slots: [],
      loading: true,
      assigningId: null,
      error: '',
      success: '',
      launched: false, // true on the launcher tab once the popup is opened
      assigned: false, // true after a successful assign → show the post-assign nav
    };
  },
  computed: {
    ...mapGetters({
      userToken: 'userToken',
      userRole: 'userRole',
      userClientID: 'userClientID',
      userID: 'userID',
    }),
    // a query-token fallback for when the page is opened outside an active
    // tenant session (the report link may carry it); prefer the store token.
    authToken() {
      if (this.userToken) return this.userToken;
      const q = (this.$route && this.$route.query) || {};
      return q.token ? 'Bearer ' + this.safeAtob(q.token) : '';
    },
    canClose() {
      return !!window.opener || window.history.length <= 1;
    },
  },
  created() {
    const q = (this.$route && this.$route.query) || {};
    this.jobId = q.job ? this.safeAtob(q.job) : (this.$route.params.job || '');
    this.employeeId = q.employee ? this.safeAtob(q.employee) : (this.$route.params.employee || '');

    // #1533 gap 1: present as a popup window, not a full-tab page nav. When opened
    // straight from the report link (no embed flag), relaunch as a sized popup and
    // hand off. Degrades to rendering inline (still a modal card) if popups are
    // blocked or the launcher tab can't be closed.
    if (!q.embed && this.relaunchAsPopup()) {
      this.launched = true;
      return;
    }

    if (!this.jobId || !this.employeeId) {
      this.loading = false;
      this.error = 'Missing job or employee reference.';
      return;
    }
    this.loadSlots();
  },
  methods: {
    // tolerate both base64'd and raw values in the link
    safeAtob(v) {
      try { return atob(v); } catch (e) { return v; }
    },
    // open this same view as a sized popup window; returns true if it took over
    relaunchAsPopup() {
      try {
        const w = 470, h = 730;
        const left = Math.max(0, ((window.screen.width || 1280) - w) / 2);
        const top = Math.max(0, ((window.screen.height || 800) - h) / 2);
        const sep = window.location.href.indexOf('?') === -1 ? '?' : '&';
        const popup = window.open(
          window.location.href + sep + 'embed=1',
          'cs3WishlistAssign',
          'width=' + w + ',height=' + h + ',left=' + left + ',top=' + top +
          ',menubar=no,toolbar=no,location=no,status=no,resizable=yes,scrollbars=yes'
        );
        if (popup) {
          try { popup.focus(); } catch (e) { /* ignore */ }
          // the launcher tab was opened by the report's window.open, so close() is
          // permitted; if it's blocked we leave the "you can close this tab" hint.
          window.setTimeout(() => { try { window.close(); } catch (e) { /* ignore */ } }, 60);
          return true;
        }
      } catch (e) { /* popups blocked → fall through to inline render */ }
      return false;
    },
    headers() {
      const h = { 'Authorization': this.authToken };
      if (this.userRole) h['cs3-context-role'] = this.userRole;
      if (this.userClientID) h['cs3-context-clientId'] = this.userClientID;
      return h;
    },
    loadSlots() {
      this.loading = true;
      this.error = '';
      service.get('job/' + this.jobId + '/eligible-open-slots/' + this.employeeId, this.headers())
        .then((res) => {
          const result = (res.data && (res.data.result || res.data.data)) || [];
          this.slots = Array.isArray(result) ? result : [];
        })
        .catch((err) => {
          console.log(err);
          this.error = 'Could not load open slots.';
        })
        .then(() => { this.loading = false; });
    },
    assign(slot) {
      if (this.assigningId) return;
      this.assigningId = slot.id;
      this.error = '';
      this.success = '';
      // user_id is required server-side for the ActionLog initiator (NOT NULL);
      // omitting it previously rolled the assign back and looked like a conflict.
      service.put('call/' + slot.id + '/assign', {
        employee_id: this.employeeId,
        user_id: this.userID,
        // #1533: an assignment from the wishlist soft-deletes the wishlist entry
        // server-side (mirrors the 911/availability assign path) so the name
        // clears from the wishlist + the summary report on the next refresh.
        from_wishlist: true,
      }, this.headers())
        .then((res) => {
          // envelope is { status_code, status_message, result } — not `status`
          const ok = res.data && (res.data.status_code === '1' || res.data.status_code === 1);
          if (ok) {
            this.success = 'Assigned and removed from the wishlist. Sent to the phone sheet.';
            // #1533: show the post-assign navigation (ed's spec). "Wish List" / "Reload"
            // force a fresh report JSON into the WL window; the rest navigate it there.
            this.assigned = true;
          } else {
            this.error = (res.data && res.data.status_message) || 'Could not assign (conflict).';
          }
        })
        .catch((err) => {
          const msg = err && err.response && err.response.data && err.response.data.status_message;
          this.error = msg || 'Assign failed — please retry.';
        })
        .then(() => { this.assigningId = null; });
    },
    // #1533: hand the post-assign nav choice to the report opener (jobDetails, same
    // origin) which drives the cross-origin report window; then close this popup.
    navTo(target) {
      // #1533: Reload WL drives the report window the dispatcher is looking at, so it is
      // handed to the MAIN jobDetails tab (localStorage 'storage' event survives this popup
      // closing; BroadcastChannel is a backup).
      if (target === 'refresh') {
        const msg = { type: 'wl-nav', target: 'refresh', job: this.jobId, t: Date.now() };
        try { localStorage.setItem('cs3-wl-nav', JSON.stringify(msg)); } catch (e) { /* ignore */ }
        try { const c = new BroadcastChannel(WL_CHANNEL); c.postMessage(msg); c.close(); } catch (e) { /* ignore */ }
        window.setTimeout(() => { try { this.closeWindow(); } catch (e) { /* ignore */ } }, 200);
        return;
      }
      // #1533 final UX: Phone Sheet + Calls + Job List ALL open a NEW tab and take focus,
      // straight from THIS click (a user gesture → not popup-blocked). The new tab boots the
      // SPA fresh and inherits the switched-client context from the persisted vuex store +
      // shared cookies (csLogin/powerSteward), exactly like Phone Sheet already does. The a2
      // $router.push navigated the already-open main tab (no new tab, no focus jump — behind
      // the report); switching to window.open is safe now that #1559 fixed the token bug
      // (dup-auth-header removed; #1556).
      //
      // #1533: "Calls" opens the JUST-ASSIGNED job's call view at /viewCalls/<btoa(jobId)>,
      // NOT the global /callList11. ViewCalls IS in the employee route allowlist (CallList is
      // commented out), so this sidesteps the powerSteward/csLogin route gate entirely — the
      // real reason the switched power steward bounced to /schedule. Mirrors every other assign
      // flow (single.vue, bulk.vue, jobList.vue:2568, dailySchedule.vue).
      const origin = window.location.origin;
      const path = target === 'phonesheet' ? '/#/phoneSheet/' + (this.jobId ? btoa(this.jobId) : '')
                 : target === 'calllist'   ? '/#/viewCalls/' + (this.jobId ? btoa(this.jobId) : '')
                 : target === 'joblist'    ? '/#/jobList'
                 : null;
      if (path) {
        const w = window.open(origin + path, '_blank');
        if (w) { try { w.focus(); } catch (e) { /* ignore */ } }
      }
      this.closeWindow();
    },
    closeWindow() {
      window.close();
    },
    formatSlot(slot) {
      const fmt = (d) => {
        if (!d) return '';
        const dt = new Date(String(d).replace(' ', 'T'));
        if (isNaN(dt.getTime())) return d;
        return dt.toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
      };
      return fmt(slot.start) + (slot.end ? ' – ' + fmt(slot.end) : '');
    },
  },
};
</script>

<style scoped>
/* #1533: render as a centered modal/popup card over a dimmed backdrop (the report
   is cross-origin + arjs is frozen, so the overlay lives on this target page) — it
   reads as a popup, not a full-tab navigation. */
.wl-assign {
  min-height: 100vh;
  background: rgba(15, 23, 42, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px 16px;
}
.wl-assign--launch { background: rgba(15, 23, 42, 0.75); }
.wl-assign__launchmsg {
  color: #fff;
  text-align: center;
  font-size: 1.05rem;
  line-height: 1.7;
}
.wl-assign__launchmsg small { color: #cbd5e1; font-size: 0.8em; }
.wl-assign__card {
  background: #fff;
  width: 440px;
  max-width: 96vw;
  max-height: 88vh;
  overflow-y: auto;
  border-radius: 10px;
  box-shadow: 0 18px 50px rgba(0, 0, 0, 0.4);
  padding: 20px 22px 24px;
}
.wl-assign__head {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.wl-assign__head h2 { font-size: 1.2rem; margin: 0; color: #333; }
.wl-assign__close { font-size: 24px; color: #999; text-decoration: none; line-height: 1; }
.wl-assign__emp { color: #555; margin: 4px 0 14px; font-weight: 600; }
.wl-assign__error { color: #b00020; background: #fdecef; padding: 10px 12px; border-radius: 4px; margin-bottom: 12px; }
.wl-assign__success { color: #1b7a36; background: #eaf6ee; padding: 10px 12px; border-radius: 4px; margin-bottom: 12px; }
.wl-assign__note { color: #777; padding: 12px 0; }
.wl-assign__slots { list-style: none; margin: 0; padding: 0; }
.wl-assign__slot {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid #eee;
}
.wl-assign__slotinfo { display: flex; flex-direction: column; }
.wl-assign__slotinfo em { color: #888; font-style: normal; font-size: 0.85em; margin-top: 2px; }
.wl-assign__btn {
  background: #2a6ebd;
  color: #fff;
  padding: 6px 16px;
  border-radius: 4px;
  text-decoration: none;
  font-size: 0.9em;
}
.wl-assign__btn.is-busy { opacity: 0.6; pointer-events: none; }
/* #1533: post-assign navigation */
.wl-assign__nav { display: flex; flex-direction: column; gap: 8px; padding: 4px 0 2px; }
.wl-assign__navhint { color: #555; font-weight: 600; margin: 2px 0 6px; }
.wl-assign__navbtn {
  display: block;
  width: 100%;
  text-align: center;
  background: #f1f5f9;
  color: #1f2937;
  border: 1px solid #d7dde5;
  padding: 11px 14px;
  border-radius: 6px;
  font-size: 0.95em;
  cursor: pointer;
}
.wl-assign__navbtn:hover { background: #e7edf4; }
.wl-assign__navbtn--primary {
  background: #2a6ebd;
  color: #fff;
  border-color: #2a6ebd;
  font-weight: 600;
}
.wl-assign__navbtn--primary:hover { background: #245ea3; }
</style>
